<template>
  <AppButton v-bind="$attrs" @click="onOpenClick()">
    <slot></slot>
  </AppButton>

  <AppModal :show="isModalOpen" @close="closeModal()">
    <h3>{{ $t("reservation.notification.title") }}</h3>

    <p>{{ $t("reservation.notification.text") }}</p>

    <ReservationNotificationForm
      v-if="isModalOpen"
      :disabled="isBusy"
      :value="currentSettings"
      @submit="onSubmit($event)"
    >
      <AppButton type="submit" :loading="isBusy">
        {{ $t("reservation.notification.button") }}
      </AppButton>
    </ReservationNotificationForm>
  </AppModal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import AppButton from "@/components/app-button.vue";
import AppModal from "@/components/app-modal.vue";
import ReservationNotificationForm from "./reservation-notification-form.vue";
import { NotificationSettings, ReservationNotificationData } from "@/types";
import { isLoggedIn, openLoginModal, user } from "../user/store";
import { useToast } from "../../utils/use-toast";
import {
  isEmailEnabled,
  isSmsEnabled,
  updateEmailNotification,
  updatePhoneNumber,
  updateSMSNotification,
} from "../settings/store";

const props = defineProps<{
  settings?: NotificationSettings;
}>();

const isModalOpen = ref(false);
const isBusy = ref(false);
const currentSettings = ref<ReservationNotificationData | null>(null);
const { success } = useToast();

const openModal = () => {
  setSettings();
  isModalOpen.value = true;
};
const closeModal = () => (isModalOpen.value = false);

// TODO: data is not used anymore. Cleanup related things.
const onSubmit = async (data: ReservationNotificationData) => {
  isBusy.value = true;
  try {
    if (
      data.enable_sms &&
      data.phone &&
      data.phone !== currentSettings.value?.phone
    ) {
      await updatePhoneNumber(data.phone);
    }
    if (data.enable_email !== !!currentSettings.value?.enable_email) {
      await updateEmailNotification(data.enable_email);
    }
    if (data.enable_sms !== !!currentSettings.value?.enable_sms) {
      await updateSMSNotification(data.enable_sms);
    }
    success("reservation.notification.success");
    closeModal();
  } finally {
    isBusy.value = false;
  }
};

const onOpenClick = async () => {
  if (isLoggedIn.value) {
    openModal();
  } else {
    const isLogged = await openLoginModal();
    if (isLogged) {
      openModal();
    }
  }
};

const setSettings = () => {
  currentSettings.value = {
    enable_email:
      isEmailEnabled.value ??
      props.settings?.reservation_reminder_email ??
      false,
    enable_sms:
      isSmsEnabled.value ??
      props.settings?.reservation_reminder_sms ??
      false,
    phone: user?.value?.phone,
  };
};
</script>
